import React from 'react';
import { useTranslation } from 'react-i18next';
import { Controller, useFormContext } from 'react-hook-form';
import { FormCheckbox } from 'app/components/form/FormCheckbox.tsx';
import {
  Box,
  FormControl,
  FormControlLabel,
  Grid,
  MenuItem,
  TextField,
  Switch
} from '@mui/material';
import { useSelector } from 'react-redux';

import { I18nNamespace } from '@/i18n/types/i18nNamespace.ts';
import type { I18nScenarioEditorNs } from '@/i18n/dictionaries/interfaces.ts';

import type { ScenarioSettingsFormProps } from '../ScenarioGeneralSettings/types.ts';
import * as RecognitionSelector from 'app/selectors/voiceRecognitionSelector.ts';
import { createFormField } from 'app/utils/formUtils.ts';

// Bridge: Use Orval Llm in UI, map to legacy LLM for form state
import { Llm } from '@/api-gen/api.schemas';
import type { ScenarioSettingsType } from 'app/components/ScenarioEditorPage/ScenarioSettings/hooks/useScenarioForm.ts';
import { isUserAdminSelector, isUserSuperAdminSelector, isUserAnyAdminSelector } from 'app/selectors/userSelectors.ts';
import * as ScenarioSelectors from 'app/selectors/scenarioSelectors.ts';
import { PdfUploadComponent } from '../ScenarioGeneralSettings/components/PdfUploadComponent.tsx';

export const ScenarioAdvancedSettings: React.FC<ScenarioSettingsFormProps> = ({ scenarioId }) => {
  const [translate] = useTranslation([I18nNamespace.ScenarioEditor]);
  const [vt] = useTranslation(I18nNamespace.Validation);

  // Create type-safe form field accessor
  const field = createFormField<ScenarioSettingsType>();

  const isDialogActive = useSelector(RecognitionSelector.isDialogActive);
  const isSuperAdmin = useSelector(isUserSuperAdminSelector);
  const scenario = useSelector(ScenarioSelectors.getScenarioById(scenarioId));

  const {
    control,
    formState: { errors },
    setValue
  } = useFormContext<ScenarioSettingsType>();
  return (
    <Box sx={{ mt: 5, mb: 3 }}>
      <Grid container>
        {isSuperAdmin ? (
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Controller
                name={field.llm}
                control={control}
                render={({ field: fieldProps }) => {
                  const uiValue: Llm | '' = fieldProps.value || '';
                  return (
                    <TextField
                      margin="normal"
                      select
                      variant="outlined"
                      disabled={isDialogActive}
                      label="LLM"
                      fullWidth
                      error={!!errors.llm}
                      helperText={errors.llm ? vt(errors.llm?.message as string) : '\u00A0'}
                      value={uiValue}
                      onChange={(e) => {
                        const selected = e.target.value as Llm;
                        fieldProps.onChange(selected);
                      }}
                    >
                      <MenuItem key={Llm.Gpt4_1} value={Llm.Gpt4_1} data-cy="ChatGPT_41">
                        GPT-4.1
                      </MenuItem>
                      <MenuItem key={Llm.Gpt4_1mini} value={Llm.Gpt4_1mini} data-cy="ChatGPT_41Mini">
                        GPT-4.1 Mini
                      </MenuItem>
                      <MenuItem key={Llm.Gpt4_1nano} value={Llm.Gpt4_1nano} data-cy="ChatGPT_41Nano">
                        GPT-4.1 Nano
                      </MenuItem>
                      <MenuItem key={Llm.Gpt4o} value={Llm.Gpt4o} data-cy="ChatGPT_4o">
                        ChatGPT-4o
                      </MenuItem>
                      <MenuItem key={Llm.Gpt4oMini} value={Llm.Gpt4oMini} data-cy="ChatGPT_4oMini">
                        ChatGPT-4o Mini
                      </MenuItem>
                      <MenuItem key={Llm.Gemini2_5Flash} value={Llm.Gemini2_5Flash} data-cy="Gemini2_5Flash">
                        Gemini 2.5 Flash
                      </MenuItem>
                      <MenuItem
                        key={Llm.Gemini2_5FlashLite}
                        value={Llm.Gemini2_5FlashLite}
                        data-cy="Gemini2_5FlashLite"
                      >
                        Gemini 2.5 Flash Lite
                      </MenuItem>
                      <MenuItem key={Llm.Gemini3Flash} value={Llm.Gemini3Flash} data-cy="Gemini3Flash">
                        Gemini 3 Flash
                      </MenuItem>
                      <MenuItem
                        key={Llm.KimiK2Instruct_0905}
                        value={Llm.KimiK2Instruct_0905}
                        data-cy="KimiK2Instruct_0905"
                      >
                        Kimi K2 Instruct 0905
                      </MenuItem>
                      <MenuItem key={Llm.Qwen3_32B} value={Llm.Qwen3_32B} data-cy="Qwen3_32B">
                        Qwen 3 32B
                      </MenuItem>
                    </TextField>
                  );
                }}
              />
            </Grid>
          </Grid>
        ) : null}

        <Grid item xs={12}>
          <FormControl variant="outlined" margin="normal">
            <FormControlLabel
              label={translate(
                nameof.full<I18nScenarioEditorNs>((n) => n.scenarioGeneralSettings.enableLargeLanguageModel)
              )}
              control={
                <Controller
                  control={control}
                  name={field.isLlmEnabled}
                  render={({ field: { value, onChange } }) => (
                    <Switch
                      checked={!!value}
                      onChange={(event) => onChange(event.target.checked)}
                      name="isLlmEnabled"
                      color="primary"
                    />
                  )}
                />
              }
            />
          </FormControl>
        </Grid>

        {isSuperAdmin ? (
          <Grid item>
            <FormCheckbox name={field.useStreaming} control={control} label="Use streaming" />
          </Grid>
        ) : null}

        {isSuperAdmin ? (
          <Grid item>
            <FormCheckbox name={field.isDecisionSupportEnabled} control={control} label="Enable decision support" />
          </Grid>
        ) : null}

        {isSuperAdmin ? (
          <Grid item xs={8}>
            <FormCheckbox name={field.createSharedScenario} control={control} label="Create shared scenario?" />
          </Grid>
        ) : null}

        {isUserAnyAdminSelector && (
          <Grid item xs={4}>
            <Controller
              name={field.maxTokens}
              control={control}
              render={({ field: fieldProps }) => (
                <TextField
                  {...fieldProps}
                  type="number"
                  margin="normal"
                  variant="outlined"
                  disabled={isDialogActive}
                  label="Max tokens"
                  placeholder="25"
                  fullWidth
                  error={!!errors.maxTokens}
                  helperText={errors.maxTokens?.message}
                />
              )}
            />
          </Grid>
        )}

        {/* Training Material Upload */}
        <PdfUploadComponent scenarioId={scenarioId} hasPdf={scenario?.hasPdf} />
      </Grid>
    </Box>
  );
};

export default ScenarioAdvancedSettings;
